/**
 * 纵向信号轮播：管理索引、自动播放、暂停状态和 Shopify 主题编辑器事件。
 * 组件不依赖第三方库；断开 DOM 时会清理计时器和全部监听器。
 */
class SignalCarousel extends HTMLElement {
  #abortController = null;
  #currentIndex = 0;
  #dots = [];
  #motionPreference = null;
  #slides = [];
  #timerId = null;
  #track = null;
  #wheelDelta = 0;
  #wheelLocked = false;
  #wheelResetTimerId = null;

  connectedCallback() {
    if (this.#abortController) return;

    this.#abortController = new AbortController();
    this.#track = this.querySelector('[data-signal-track]');
    this.#slides = Array.from(this.querySelectorAll('[data-signal-slide]'));
    this.#dots = Array.from(this.querySelectorAll('[data-signal-dot]'));
    this.#motionPreference = window.matchMedia('(prefers-reduced-motion: reduce)');

    if (!this.#track || this.#slides.length === 0) {
      console.warn('Signal carousel requires a track and at least one slide.');
      return;
    }

    this.setAttribute('data-ready', 'true');
    this.#bindEvents();
    this.showSlide(0);
    this.#startAutoplay();
  }

  disconnectedCallback() {
    this.#stopAutoplay();
    this.#resetWheelGesture();
    this.#abortController?.abort();
    this.#abortController = null;
  }

  /**
   * 显示指定帧，并同步轨道、分页点和无障碍状态。
   * @param {number} requestedIndex 目标帧索引。
   * @param {boolean} restartAutoplay 手动操作后是否重启自动播放计时。
   */
  showSlide(requestedIndex, restartAutoplay = false) {
    if (!Number.isInteger(requestedIndex) || this.#slides.length === 0) return;

    this.#currentIndex = (requestedIndex + this.#slides.length) % this.#slides.length;
    this.style.setProperty('--app-guide-signal-index', String(this.#currentIndex));

    for (const [index, slide] of this.#slides.entries()) {
      const isActive = index === this.#currentIndex;
      slide.setAttribute('aria-hidden', String(!isActive));
      slide.inert = !isActive;
    }

    for (const [index, dot] of this.#dots.entries()) {
      if (index === this.#currentIndex) {
        dot.setAttribute('aria-current', 'true');
      } else {
        dot.removeAttribute('aria-current');
      }
    }

    const status = this.querySelector('[data-signal-status]');
    if (status) {
      status.textContent = `Slide ${this.#currentIndex + 1} of ${this.#slides.length}`;
    }

    if (restartAutoplay) this.#restartAutoplay();
  }

  #bindEvents() {
    const { signal } = this.#abortController;

    this.addEventListener('click', (event) => this.#handleClick(event), { signal });
    this.addEventListener('wheel', (event) => this.#handleWheel(event), { signal, passive: false });
    this.addEventListener('pointerenter', () => this.#stopAutoplay(), { signal });
    this.addEventListener('pointerleave', () => this.#startAutoplay(), { signal });
    this.addEventListener('focusin', () => this.#stopAutoplay(), { signal });
    this.addEventListener('focusout', () => this.#handleFocusOut(), { signal });
    this.addEventListener('shopify:block:select', (event) => this.#handleBlockSelect(event), { signal });
    this.addEventListener('shopify:block:deselect', () => this.#startAutoplay(), { signal });
    document.addEventListener('visibilitychange', () => this.#handleVisibilityChange(), { signal });
    this.#motionPreference.addEventListener('change', () => this.#handleMotionChange(), { signal });
  }

  #handleClick(event) {
    const button = event.target.closest('button');
    if (!button || !this.contains(button)) return;

    if (!button.matches('[data-signal-dot]')) return;

    const slideIndex = Number.parseInt(button.dataset.slideIndex, 10);
    if (Number.isInteger(slideIndex)) this.showSlide(slideIndex, true);
  }

  /**
   * 使用鼠标滚轮按视觉方向切换帧。连续触控板事件只触发一次切换，
   * 并在第一帧向上或最后一帧向下时放行页面原生滚动。
   * @param {WheelEvent} event 滚轮事件。
   */
  #handleWheel(event) {
    if (this.#slides.length < 2 || event.ctrlKey) return;

    const deltaMultiplier = event.deltaMode === WheelEvent.DOM_DELTA_LINE
      ? 16
      : event.deltaMode === WheelEvent.DOM_DELTA_PAGE
        ? this.clientHeight
        : 1;
    const verticalDelta = event.deltaY * deltaMultiplier;
    const horizontalDelta = event.deltaX * deltaMultiplier;

    if (!Number.isFinite(verticalDelta) || Math.abs(verticalDelta) <= Math.abs(horizontalDelta)) return;

    if (this.#wheelLocked) {
      event.preventDefault();
      this.#scheduleWheelReset();
      return;
    }

    const direction = verticalDelta > 0 ? 1 : -1;
    const isAtFirstSlide = direction < 0 && this.#currentIndex === 0;
    const isAtLastSlide = direction > 0 && this.#currentIndex === this.#slides.length - 1;
    if (isAtFirstSlide || isAtLastSlide) return;

    event.preventDefault();
    this.#wheelDelta += verticalDelta;
    this.#scheduleWheelReset();

    if (Math.abs(this.#wheelDelta) < 30) return;

    this.#wheelLocked = true;
    this.#wheelDelta = 0;
    this.showSlide(this.#currentIndex + direction, true);
  }

  #scheduleWheelReset() {
    if (this.#wheelResetTimerId !== null) window.clearTimeout(this.#wheelResetTimerId);

    this.#wheelResetTimerId = window.setTimeout(() => {
      this.#wheelDelta = 0;
      this.#wheelLocked = false;
      this.#wheelResetTimerId = null;
    }, 240);
  }

  #resetWheelGesture() {
    if (this.#wheelResetTimerId !== null) window.clearTimeout(this.#wheelResetTimerId);
    this.#wheelDelta = 0;
    this.#wheelLocked = false;
    this.#wheelResetTimerId = null;
  }

  #handleFocusOut() {
    window.requestAnimationFrame(() => {
      if (!this.contains(document.activeElement)) this.#startAutoplay();
    });
  }

  #handleBlockSelect(event) {
    const selectedSlide = event.target.closest('[data-signal-slide]');
    if (!selectedSlide || !this.contains(selectedSlide)) return;

    const selectedIndex = this.#slides.indexOf(selectedSlide);
    if (selectedIndex < 0) return;

    this.#stopAutoplay();
    this.showSlide(selectedIndex);
  }

  #handleVisibilityChange() {
    if (document.hidden) {
      this.#stopAutoplay();
      return;
    }

    this.#startAutoplay();
  }

  #handleMotionChange() {
    if (this.#motionPreference.matches) {
      this.#stopAutoplay();
      return;
    }

    this.#startAutoplay();
  }

  #restartAutoplay() {
    this.#stopAutoplay();
    this.#startAutoplay();
  }

  #startAutoplay() {
    if (this.#timerId !== null) return;
    if (this.dataset.autoplay !== 'true') return;
    if (this.#slides.length < 2 || this.#motionPreference?.matches || document.hidden) return;
    if (this.matches(':hover') || this.contains(document.activeElement)) return;

    const configuredInterval = Number.parseInt(this.dataset.interval, 10);
    const interval = Number.isFinite(configuredInterval) && configuredInterval >= 1000
      ? configuredInterval
      : 5000;

    this.#timerId = window.setInterval(() => {
      this.showSlide(this.#currentIndex + 1);
    }, interval);
  }

  #stopAutoplay() {
    if (this.#timerId === null) return;
    window.clearInterval(this.#timerId);
    this.#timerId = null;
  }
}

if (!customElements.get('signal-carousel')) {
  customElements.define('signal-carousel', SignalCarousel);
}
